/**
 * Pure month-grid + day-bucketing helpers for the calendar-aware date picker
 * (SCH.1). Kept separate from the component so the date math is unit-tested.
 *
 * All day strings are business-local 'YYYY-MM-DD' built from local date parts
 * (never UTC ISO slicing), matching the calendar endpoint's day strings.
 */

export interface CalendarCell {
  /** 'YYYY-MM-DD' */
  day: string;
  /** belongs to the month being displayed (vs the leading/trailing spill) */
  inMonth: boolean;
  isToday: boolean;
}

export function toDayStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse 'YYYY-MM-DD' to a local-midnight Date (no TZ drift). */
export function parseDay(s: string): Date {
  const parts = s.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(y, m - 1, d);
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthLabel(year: number, month0: number): string {
  return `${MONTHS[month0]} ${year}`;
}

/**
 * Convert a picked 'YYYY-MM-DD' to the ISO datetime the API stores
 * (requested_start_date etc.). Local-midnight → UTC, matching the form intake's
 * convention so a day chosen on the picker lands on the same business day.
 */
export function dayToIso(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString();
}

/** Inverse of dayToIso: an ISO datetime → the local 'YYYY-MM-DD' for the picker. */
export function isoToDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : toDayStr(d);
}

/** An ISO datetime → local 'HH:MM' for a <input type="time"> default. */
export function isoToTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Combine a 'YYYY-MM-DD' day + 'HH:MM' time (local) into a stored ISO datetime. */
export function dayTimeToIso(day: string, time: string): string {
  return new Date(`${day}T${time}:00`).toISOString();
}

/** Format an 'HH:MM' (24-hour) string as a 12-hour clock, e.g. '8:00 AM'. */
export function hhmmTo12(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * A compact "requested hours" label from a per-day schedule: one window when
 * every day shares the same hours, "Varies by day" when they differ, and a
 * flexible note when the requester left hours open. Null when there's nothing.
 */
export function scheduleHoursLabel(
  schedule: { start_time: string; end_time: string }[] | null | undefined,
  flexible?: boolean,
): string | null {
  if (flexible) return 'Flexible — crew sets hours on site';
  if (!schedule || schedule.length === 0) return null;
  const first = schedule[0]!;
  const same = schedule.every(
    (d) => d.start_time === first.start_time && d.end_time === first.end_time,
  );
  return same ? `${hhmmTo12(first.start_time)} – ${hhmmTo12(first.end_time)}` : 'Varies by day';
}

export function addMonths(
  year: number,
  month0: number,
  delta: number,
): { year: number; month0: number } {
  const d = new Date(year, month0 + delta, 1);
  return { year: d.getFullYear(), month0: d.getMonth() };
}

/**
 * 42-cell (6-week), Sunday-start grid covering the given month plus the
 * leading/trailing days needed to fill whole weeks.
 */
export function monthGrid(
  year: number,
  month0: number,
  today: string,
): CalendarCell[] {
  const first = new Date(year, month0, 1);
  const startOffset = first.getDay(); // 0 = Sunday
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(year, month0, 1 - startOffset + i);
    const day = toDayStr(d);
    cells.push({ day, inMonth: d.getMonth() === month0, isToday: day === today });
  }
  return cells;
}

/**
 * Bucket calendar items onto every day in their inclusive [start_date, end_date]
 * span, so a multi-day project shows on each of its days. Guarded against bad
 * ranges so a malformed span can't loop forever.
 */
export function bucketByDay<T extends { start_date: string; end_date: string }>(
  items: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const it of items) {
    let d = parseDay(it.start_date);
    const end = parseDay(it.end_date);
    let guard = 0;
    while (d <= end && guard < 370) {
      const key = toDayStr(d);
      const arr = map.get(key);
      if (arr) arr.push(it);
      else map.set(key, [it]);
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      guard += 1;
    }
  }
  return map;
}
